import { Injectable } from '@angular/core';
import { PersonalizationKeysEnum } from '@docma-mm-core/personalization/personalization-keys.enum';
import { PersonalizationService } from '@docma-mm-core/personalization/personalization.service';
import { BehaviorSubject } from 'rxjs';
import { ImageDownloadsize } from '../enums/image-download-size.enum';
import { FaultUploadType } from '../enums/fault-upload-type.enum';
import { AttachmentDownloadOption } from '../enums/attachment-download-option.enum';

export const ImageDownloadDimensions: Record<ImageDownloadsize, { width: number; height: number } | undefined> = {
  [ImageDownloadsize.Small]: { width: 800, height: 800 },
  [ImageDownloadsize.Medium]: { width: 1024, height: 1024 },
  [ImageDownloadsize.Large]: { width: 1600, height: 1600 },
  [ImageDownloadsize.Original]: undefined
};

@Injectable({
  providedIn: 'root'
})
export class CapacitorAppSettingsService {
  private databasePrefix = 'CapacitorAppSettings.';
  private imageDownloadSizeSubject = new BehaviorSubject<ImageDownloadsize>(ImageDownloadsize.Original);
  downloadImageSize$ = this.imageDownloadSizeSubject.asObservable();

  private attachmentAutomaticDownloadSubject = new BehaviorSubject<AttachmentDownloadOption>(AttachmentDownloadOption.WifiAndCellular);
  attachmentAutomaticDownload$ = this.attachmentAutomaticDownloadSubject.asObservable();

  private faultUploadTypeSubject = new BehaviorSubject<FaultUploadType>(FaultUploadType.WifiAndCellular);
  faultUploadType$ = this.faultUploadTypeSubject.asObservable();

  constructor(private personalizationService: PersonalizationService) {
    this.loadSettings();
  }

  private async loadSettings() {
    const imageDownloadSize = await this.personalizationService.getValue(this.databasePrefix + PersonalizationKeysEnum.ImageDownloadSize);
    if (imageDownloadSize) {
      this.imageDownloadSizeSubject.next(imageDownloadSize);
    }

    const attachmentDownload = await this.personalizationService.getValue(this.databasePrefix + PersonalizationKeysEnum.AttachmentDownloadOption);
    if (attachmentDownload) {
      this.attachmentAutomaticDownloadSubject.next(attachmentDownload);
    }

    const uploadType = await this.personalizationService.getValue(this.databasePrefix + PersonalizationKeysEnum.FaultUploadType);
    if (uploadType) {
      this.faultUploadTypeSubject.next(uploadType);
    }
  }

  async setValue(key: string, value: any): Promise<any> {
    await this.personalizationService.setValue(this.databasePrefix + key, value);
    switch (key) {
      case PersonalizationKeysEnum.ImageDownloadSize:
        this.imageDownloadSizeSubject.next(value);
        break;
      case PersonalizationKeysEnum.AttachmentDownloadOption:
        this.attachmentAutomaticDownloadSubject.next(value);
        break;
      case PersonalizationKeysEnum.FaultUploadType:
        this.faultUploadTypeSubject.next(value);
        break;
      default:
        console.warn(`Unhandled setting key: ${key}`);
    }
  }

  get currentImageDownloadSize() {
    const size = this.imageDownloadSizeSubject.getValue();
    return ImageDownloadDimensions[size];
  }

  get currentImageDownloadSizeEnum() {
    return this.imageDownloadSizeSubject.getValue();
  }

  get currentAttachmentAutomaticDownload() {
    return this.attachmentAutomaticDownloadSubject.getValue();
  }

  get currentFaultUploadType() {
    return this.faultUploadTypeSubject.getValue();
  }
}
